import { DOCUMENT_TYPE_META } from '../constants';
import type { Policy, PolicyDocument } from '../types';
import { formatCurrency, formatDate } from '../utils';

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-dashed py-1.5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
    <span className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</span>
    <span className="text-[13px] font-semibold text-neutral-800">{value}</span>
  </div>
);

export const PdfPreview: React.FC<{ doc: PolicyDocument; policy: Policy }> = ({ doc, policy }) => {
  const meta = DOCUMENT_TYPE_META[doc.type];
  return (
    <div className="flex justify-center overflow-auto rounded-lg p-4" style={{ backgroundColor: 'var(--color-neutral-300)' }}>
      {/* Fixed light "paper" — intentionally not theme-aware, like a real PDF. */}
      <div
        className="relative w-full max-w-[520px] rounded-sm bg-white px-8 py-8 text-neutral-900 shadow-xl"
        style={{ aspectRatio: '8.5 / 11' }}
      >
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
          <span className="rotate-[-30deg] whitespace-nowrap text-6xl font-black text-neutral-100 select-none">PREVIEW</span>
        </span>

        <div className="relative">
          <div className="flex items-start justify-between border-b-2 pb-3" style={{ borderColor: 'var(--color-primary-500)' }}>
            <div>
              <p className="text-lg font-black" style={{ color: 'var(--color-primary-700)' }}>
                {policy.carrier.name}
              </p>
              <p className="text-[11px] text-neutral-500">NAIC {policy.carrier.naicCode} · A.M. Best {policy.carrier.amBestRating}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">{meta.label}</p>
              <p className="text-sm font-bold text-neutral-800">{policy.policyNumber}</p>
            </div>
          </div>

          <h2 className="mt-5 text-center text-base font-bold uppercase tracking-wide text-neutral-800">
            {meta.label}
          </h2>

          <div className="mt-5 space-y-0.5">
            <Row label="Named Insured" value={policy.insured.name} />
            <Row label="Product" value={policy.product} />
            <Row label="Policy Period" value={`${formatDate(policy.effectiveDate)} – ${formatDate(policy.expirationDate)}`} />
            <Row label="Producing Broker" value={`${policy.broker.name}, ${policy.broker.agency}`} />
            {doc.type === 'invoice' ? (
              <>
                <Row label="Annual Premium" value={formatCurrency(policy.annualPremium)} />
                <Row label="Taxes & Fees" value={formatCurrency(Math.round(policy.annualPremium * 0.021))} />
                <Row label="Total Due" value={formatCurrency(Math.round(policy.annualPremium * 1.021))} />
              </>
            ) : (
              <>
                <Row label="Benefit Level" value={`${policy.coverage.benefitPercent}% of wages`} />
                <Row label="Weekly Maximum" value={formatCurrency(policy.coverage.weeklyCap)} />
                <Row label="Leave Duration" value={`${policy.coverage.lengthOfLeaveWeeks} weeks`} />
                <Row label="Waiting Period" value={`${policy.coverage.waitingPeriodDays} days`} />
                <Row label="Aggregate Limit" value={formatCurrency(policy.coverage.aggregateLimit)} />
                <Row label="Annual Premium" value={formatCurrency(policy.annualPremium)} />
              </>
            )}
          </div>

          <p className="mt-6 text-[10px] leading-relaxed text-neutral-400">
            This document is a demonstration preview generated by the Parento Policy Administration System. It does not
            constitute a binding contract of insurance. Terms, conditions, and exclusions are governed by the executed
            policy contract on file with {policy.carrier.name}.
          </p>

          <div className="mt-4 flex items-center justify-between border-t pt-2 text-[10px] text-neutral-400" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <span>Generated {formatDate(doc.generatedAt)} · v{doc.version}</span>
            <span>Page 1 of {doc.pages}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
